import z from "zod";
import { ChangeRequestOperationType } from "./enums/ChangeRequestOperationType";
import { PaymentMethodType } from "./enums/PaymentMethodType";
import {
  CourseIdScheme,
  DeviceConfigIdScheme,
  OfferIdScheme,
  PaymentMethodIdScheme,
  PrinterIdScheme,
  SupplierIdScheme,
} from "./ids";
import { RestUpdateStore } from "./store";
import { RestUpdateOnlineOrderSettings } from "./onlineOrderSettings";
import { RestCreateDeviceConfig, RestUpdateDeviceConfig } from "./deviceConfig";
import { RestCreatePaymentMethod } from "./paymentMethods/paymentMethod";
import { RestCreatePrinter, RestUpdatePrinter } from "./printer";
import { RestCreateCourse, RestUpdateCourse } from "./course";
import { RestCreateOffer, RestUpdateOffer } from "./offer";
import { RestCreateSupplier, RestUpdateSupplier } from "./supplier";

export const AGENT_CREATABLE_PAYMENT_METHOD_TYPES = [
  PaymentMethodType.CASH,
  PaymentMethodType.SWISH_COMPANY,
  PaymentMethodType.OTHER,
  PaymentMethodType.GIFT_CARD,
  PaymentMethodType.CREDIT_NOTE,
];

export const ChangeRequestStoreSettingsChangesScheme = RestUpdateStore.pick({
  name: true,
  displayName: true,
  city: true,
  street: true,
  postalcode: true,
  contactPerson: true,
  contactPhone: true,
  contactEmail: true,
  language: true,
  layoutId: true,
  bookkeepingSeries: true,
  bookkeepingTextOnVoucher: true,
  bookkeepingCostcenter: true,
  bookkeepingDefaultAccounts: true,
  automaticEndOfDayTime: true,
  reportEmail: true,
  emailReport: true,
  emailSalesReportOnEndOfDay: true,
  paymentReportGrouping: true,
  receiptText: true,
  showReferenceOnReceipt: true,
  defaultReceiptPrinter: true,
  printRefundSignatureReceipt: true,
  printPayoutSignatureReceipt: true,
  printReceiptAutomatically: true,
  emailReceiptEnabled: true,
  printNewGiftcardAfterPurchase: true,
  printNewCreditNoteAfterPurchase: true,
  showArticleNumberInReceipt: true,
  showDisplayNameOnReceipt: true,
  showBarcodeOnReceipt: true,
  showReceiptQr: true,
  paymentCompleteAutoCloseSeconds: true,
  enablePriceInDecimals: true,
  enableOpenPriceInDecimals: true,
  keypadInverted: true,
  keypadDecimalEntryMode: true,
  restoreSaleAfterRestart: true,
  enableBarcodeScanner: true,
  enableCameraScanning: true,
  receiptLogoBinarizationThreshold: true,
  numberOfColumnsInStore: true,
  productStyle: true,
  paymentMethodsOrder: true,
  paymentMethodButtonLayout: true,
  customerDisplayActive: true,
  customerDisplayScaling: true,
  giftCardValidityDays: true,
  creditNoteValidityDays: true,
  extraProductFields: true,
  recoverSale: true,
  barcodeGenerationType: true,
  barcodeGenerationPrefix: true,
  cartMergeMode: true,
  cartOrderMode: true,
  fullscreenFloorPlan: true,
  tipPromptBehavior: true,
  prepTicketPromptMode: true,
  referenceDigitCount: true,
  serviceOrderDepositVatMode: true,
  serviceOrderPrinterId: true,
  serviceOrderText: true,
  serviceOrderWorkflow: true,
  tipEntryMode: true,
  tipGrid: true,
  tipShowTipAmountOnPresetButtons: true,
  tipShowSkipTipButton: true,
  showStockInStore: true,
  outOfStockBehavior: true,
  variantInputMode: true,
  staffSelectionMode: true,
  autoSignOutAfterMinutes: true,
  signOutAfterSale: true,
  hideChangeUserButton: true,
  showStaffOnReceipt: true,
  alwaysApplyRounding: true,
  defaultVAT: true,
});

export const ChangeRequestOnlineOrderSettingsChangesScheme =
  RestUpdateOnlineOrderSettings.omit({ logo: true });

const deviceConfigManagedOutsideAgent = {
  customerDisplayImage: true,
  customerDisplaySettings: true,
  cashboxId: true,
} as const;

export const ChangeRequestDeviceConfigCreatePayloadScheme =
  RestCreateDeviceConfig.omit(deviceConfigManagedOutsideAgent);

export const ChangeRequestDeviceConfigUpdateChangesScheme =
  RestUpdateDeviceConfig.omit(deviceConfigManagedOutsideAgent);

export const ChangeRequestPaymentMethodCreatePayloadScheme =
  RestCreatePaymentMethod.refine(
    (paymentMethod) =>
      AGENT_CREATABLE_PAYMENT_METHOD_TYPES.includes(paymentMethod.type),
    {
      message: `Only ${AGENT_CREATABLE_PAYMENT_METHOD_TYPES.join(", ")} can be created here; card terminals and invoice need an integration set up in the admin.`,
    },
  );

export const ChangeRequestPaymentMethodUpdateChangesScheme = z
  .object({
    name: z.string(),
    color: z.string().nullable(),
    bookkeepingAccountId: z.string().nullable(),
    openCashDrawer: z.boolean(),
    useChangeCalculator: z.boolean(),
    askForAmount: z.boolean(),
    swishNumber: z.string(),
    messageToCustomer: z.string(),
  })
  .partial()
  .strict();

const singletonUpdate = <
  TType extends ChangeRequestOperationType,
  TChanges extends z.ZodType,
>(
  type: TType,
  changes: TChanges,
) =>
  z.object({
    operationId: z.string().min(1),
    type: z.literal(type),
    payload: z.object({ changes }),
  });

export const ChangeRequestStoreSettingsOperationStrictScheme =
  z.discriminatedUnion("type", [
    singletonUpdate(
      ChangeRequestOperationType.StoreSettingsUpdate,
      ChangeRequestStoreSettingsChangesScheme,
    ),
  ]);

export const ChangeRequestOnlineOrderSettingsOperationStrictScheme =
  z.discriminatedUnion("type", [
    singletonUpdate(
      ChangeRequestOperationType.OnlineOrderSettingsUpdate,
      ChangeRequestOnlineOrderSettingsChangesScheme,
    ),
  ]);

export const ChangeRequestDeviceConfigOperationStrictScheme =
  z.discriminatedUnion("type", [
    z.object({
      operationId: z.string().min(1),
      type: z.literal(ChangeRequestOperationType.DeviceConfigCreate),
      payload: ChangeRequestDeviceConfigCreatePayloadScheme,
    }),
    z.object({
      operationId: z.string().min(1),
      type: z.literal(ChangeRequestOperationType.DeviceConfigUpdate),
      payload: z.object({
        deviceConfigId: DeviceConfigIdScheme,
        changes: ChangeRequestDeviceConfigUpdateChangesScheme,
      }),
    }),
    z.object({
      operationId: z.string().min(1),
      type: z.literal(ChangeRequestOperationType.DeviceConfigDelete),
      payload: z.object({ deviceConfigId: DeviceConfigIdScheme }),
    }),
  ]);

export const ChangeRequestPaymentMethodOperationStrictScheme =
  z.discriminatedUnion("type", [
    z.object({
      operationId: z.string().min(1),
      type: z.literal(ChangeRequestOperationType.PaymentMethodCreate),
      payload: ChangeRequestPaymentMethodCreatePayloadScheme,
    }),
    z.object({
      operationId: z.string().min(1),
      type: z.literal(ChangeRequestOperationType.PaymentMethodUpdate),
      payload: z.object({
        paymentMethodId: PaymentMethodIdScheme,
        changes: ChangeRequestPaymentMethodUpdateChangesScheme,
      }),
    }),
    z.object({
      operationId: z.string().min(1),
      type: z.literal(ChangeRequestOperationType.PaymentMethodDelete),
      payload: z.object({ paymentMethodId: PaymentMethodIdScheme }),
    }),
  ]);

export const ChangeRequestPrinterOperationStrictScheme = z.discriminatedUnion(
  "type",
  [
    z.object({
      operationId: z.string().min(1),
      type: z.literal(ChangeRequestOperationType.PrinterCreate),
      payload: RestCreatePrinter,
    }),
    z.object({
      operationId: z.string().min(1),
      type: z.literal(ChangeRequestOperationType.PrinterUpdate),
      payload: z.object({
        printerId: PrinterIdScheme,
        changes: RestUpdatePrinter,
      }),
    }),
    z.object({
      operationId: z.string().min(1),
      type: z.literal(ChangeRequestOperationType.PrinterDelete),
      payload: z.object({ printerId: PrinterIdScheme }),
    }),
  ],
);

export const ChangeRequestCourseOperationStrictScheme = z.discriminatedUnion(
  "type",
  [
    z.object({
      operationId: z.string().min(1),
      type: z.literal(ChangeRequestOperationType.CourseCreate),
      payload: RestCreateCourse.omit({ origin: true }),
    }),
    z.object({
      operationId: z.string().min(1),
      type: z.literal(ChangeRequestOperationType.CourseUpdate),
      payload: z.object({
        courseId: CourseIdScheme,
        changes: RestUpdateCourse.omit({ origin: true }),
      }),
    }),
    z.object({
      operationId: z.string().min(1),
      type: z.literal(ChangeRequestOperationType.CourseDelete),
      payload: z.object({ courseId: CourseIdScheme }),
    }),
  ],
);

export const ChangeRequestOfferOperationStrictScheme = z.discriminatedUnion(
  "type",
  [
    z.object({
      operationId: z.string().min(1),
      type: z.literal(ChangeRequestOperationType.OfferCreate),
      payload: RestCreateOffer,
    }),
    z.object({
      operationId: z.string().min(1),
      type: z.literal(ChangeRequestOperationType.OfferUpdate),
      payload: z.object({
        offerId: OfferIdScheme,
        changes: RestUpdateOffer,
      }),
    }),
    z.object({
      operationId: z.string().min(1),
      type: z.literal(ChangeRequestOperationType.OfferDelete),
      payload: z.object({ offerId: OfferIdScheme }),
    }),
  ],
);

export const ChangeRequestSupplierOperationStrictScheme = z.discriminatedUnion(
  "type",
  [
    z.object({
      operationId: z.string().min(1),
      type: z.literal(ChangeRequestOperationType.SupplierCreate),
      payload: RestCreateSupplier.omit({ origin: true }),
    }),
    z.object({
      operationId: z.string().min(1),
      type: z.literal(ChangeRequestOperationType.SupplierUpdate),
      payload: z.object({
        supplierId: SupplierIdScheme,
        changes: RestUpdateSupplier.omit({ origin: true }),
      }),
    }),
    z.object({
      operationId: z.string().min(1),
      type: z.literal(ChangeRequestOperationType.SupplierDelete),
      payload: z.object({ supplierId: SupplierIdScheme }),
    }),
  ],
);

export type ChangeRequestStoreSettingsOperation = z.infer<
  typeof ChangeRequestStoreSettingsOperationStrictScheme
>;
export type ChangeRequestOnlineOrderSettingsOperation = z.infer<
  typeof ChangeRequestOnlineOrderSettingsOperationStrictScheme
>;
export type ChangeRequestDeviceConfigOperation = z.infer<
  typeof ChangeRequestDeviceConfigOperationStrictScheme
>;
export type ChangeRequestPaymentMethodOperation = z.infer<
  typeof ChangeRequestPaymentMethodOperationStrictScheme
>;
export type ChangeRequestPrinterOperation = z.infer<
  typeof ChangeRequestPrinterOperationStrictScheme
>;
export type ChangeRequestCourseOperation = z.infer<
  typeof ChangeRequestCourseOperationStrictScheme
>;
export type ChangeRequestOfferOperation = z.infer<
  typeof ChangeRequestOfferOperationStrictScheme
>;
export type ChangeRequestSupplierOperation = z.infer<
  typeof ChangeRequestSupplierOperationStrictScheme
>;
